<template>
  <!-- 旧動線の OptionSection と新動線の CompactOptionSection で共有する中身。動線で違うのは見出しの有無（slot で受ける）と、選択肢 1 件の通訳セレクトを出すかだけ -->
  <div>
    <slot name="heading" />
    <WrappedSelectBox
      v-if="isInterpreterOptionSelectShown"
      v-model="form.step2.experienceEventInterpreterOption.id"
      aria-label="interpreterOptionChoices"
      label="Interpretation"
      lang="en"
      :default-value="defaultInterpreterOptionId"
      :disabled="props.experienceEvent.id === ''"
      :error="getErrorMessage('step2.experienceEventInterpreterOption')"
      :options="interpreterOptionChoices"
      :required="true"
      @update:model-value="handleInterpreterOptionChange"
    />
    <ExperienceEventOptionSection
      v-show="experienceOptions.length > 0"
      :default-options-form-list="form.step2.experienceEventOptions"
      :disabled="props.experienceEvent.id === ''"
      :experience-options="experienceOptions"
      :is-public-price="props.experienceEvent.experience.isPublicPrice"
      :on-change="onChangeOption"
    />
  </div>
</template>

<script setup lang="ts">
import { type Options } from '@/models/option';
import { buildWabunkaExperienceEventInterpreterOptionChoices } from '@/models/wabunka/wabunkaExperienceEventInterpreterOption';
import { type WabunkaInterpreterOptionViewModel } from '@/models/wabunka/wabunkaInterpreterOption';
import { WrappedSelectBox } from '@/wabunka-src/common/components';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import type { WabunkaReservationExperienceEventOptionForm } from '@/wabunka-src/features/Experience';
import { ExperienceEventOptionSection } from '@/wabunka-src/features/Experience';
import { type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';

interface Props {
  /** エラーメッセージを取得する関数 */
  getErrorMessage: (s: string) => string;
  /** 体験イベントの情報 */
  experienceEvent: WabunkaExperienceEventViewModel;
  /** 通訳の選択肢が 1 件しか無いとき、選びようのないセレクトを出さない（新動線だけが立てる。D-038） */
  hidesSingleInterpreterChoice?: boolean;
}
const props = defineProps<Props>();

const { form } = useGlobalWabunkaReservationForm();

// 通訳オプションのセレクトボックス選択肢
const interpreterOptionChoices = computed<Options>(() => {
  const choices = buildWabunkaExperienceEventInterpreterOptionChoices(props.experienceEvent.interpreterOptions, props.experienceEvent.experience.isPublicPrice);
  return choices;
});
const isInterpreterOptionSelectShown = computed(() => !props.hidesSingleInterpreterChoice || interpreterOptionChoices.value.length !== 1);
// 通訳オプションの選択肢の初期値
const defaultInterpreterOptionId = ref<string>(form.value.step2.experienceEventInterpreterOption.id);
// 体験マスタからのデータを参照するための初期値用
const defaultInterpreterOption = ref<WabunkaInterpreterOptionViewModel | undefined>();
// 通訳オプションが書き換えられるケースは以下
// 1. 初回ロード
// 2. 体験イベントが選択されたとき
// 3. 他の予約導線から戻ってきた時
watch(
  interpreterOptionChoices,
  () => {
    const shouldUpdateDefault =
      interpreterOptionChoices.value.length > 0 &&
      (!form.value.step2.experienceEventInterpreterOption.id || !interpreterOptionChoices.value.some((option) => option.value === form.value.step2.experienceEventInterpreterOption.id));

    if (shouldUpdateDefault) {
      const firstChoiceId = interpreterOptionChoices.value[0].value as string;
      defaultInterpreterOptionId.value = firstChoiceId;
      defaultInterpreterOption.value = props.experienceEvent.experience.interpreterOptions.find((interpreterOption) => interpreterOption.id === firstChoiceId);
      // 既定値をフォームへ入れていたのは描画された SelectBox だったため、選択欄を出さない体験でも送信値に入るようここで入れる
      const firstInterpreterOption = props.experienceEvent.interpreterOptions.find((interpreterOption) => interpreterOption.id === firstChoiceId);
      if (firstInterpreterOption) form.value.step2.experienceEventInterpreterOption = { ...firstInterpreterOption };
    }
  },
  { immediate: true },
);
const experienceOptions = computed(() => {
  return props.experienceEvent.id !== '' ? props.experienceEvent.experienceEventOptions : props.experienceEvent.experience.experienceOptions;
});
// 通訳オプションが選択されたら、そのidに紐づく情報もReservationFormに保持させる
watch(
  () => form.value.step2.experienceEventInterpreterOption.id,
  () => {
    const selectedInterpreterOption = props.experienceEvent.interpreterOptions.find((interpreterOption) => interpreterOption.id === form.value.step2.experienceEventInterpreterOption.id);
    if (!selectedInterpreterOption) return;
    form.value.step2.experienceEventInterpreterOption = {
      ...selectedInterpreterOption,
    };
  },
);
// オプションを変更したときのイベントハンドラ
const onChangeOption = (options: WabunkaReservationExperienceEventOptionForm[]) => {
  form.value.step2.experienceEventOptions = options;
};

const handleInterpreterOptionChange = (value: string) => {
  const selectedInterpreterOption = props.experienceEvent.interpreterOptions.find((interpreterOption) => interpreterOption.id === value);
  if (!selectedInterpreterOption) {
    console.error(`Interpreter option with id ${value} not found`);
    return;
  }

  form.value.step2.experienceEventInterpreterOption = {
    ...selectedInterpreterOption,
  };
};
</script>
